import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wallet, 
  Briefcase, 
  AlertCircle, 
  FileText,
  ArrowRight,
  Clock,
  CheckCircle,
  Bot,
  X,
  TrendingUp,
  TrendingDown,
  Calculator,
  Calendar,
  AlertTriangle,
  BarChart3,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { AIOrb } from '../visual-engine/components/AIOrb';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick action configurations
const quickActions = [
  { id: 'check_finances', label: 'Check Finances', icon: Calculator, requiresFinance: true },
  { id: 'plan_week', label: 'Plan the Week', icon: Calendar },
  { id: 'risks_delays', label: 'Risks & Delays', icon: AlertTriangle },
  { id: 'business_performance', label: 'Performance', icon: BarChart3 },
];

export function CommandCentre() {
  const { hasFinanceAccess } = useAuth();
  const canAccessFinance = hasFinanceAccess();
  
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [jobsToday, setJobsToday] = useState([]);
  const [quotesAwaiting, setQuotesAwaiting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  const getOrgId = useCallback(() => {
    return localStorage.getItem('org_id') || localStorage.getItem('current_org_id');
  }, []);

  const fetchData = useCallback(async () => {
    const orgId = getOrgId();
    const headers = getAuthHeaders();

    if (!orgId || !headers.Authorization || headers.Authorization === 'Bearer null') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const [summaryRes, jobsTodayRes, quotesRes] = await Promise.all([
        fetch(`${API_URL}/api/command-centre/${orgId}/summary`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/command-centre/${orgId}/jobs-today`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/command-centre/${orgId}/quotes-awaiting`, { headers }).catch(() => null)
      ]);

      if (summaryRes && summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      if (jobsTodayRes && jobsTodayRes.ok) {
        const jobsData = await jobsTodayRes.json();
        setJobsToday(jobsData);
      }

      if (quotesRes && quotesRes.ok) {
        const quotesData = await quotesRes.json();
        setQuotesAwaiting(quotesData);
      }

    } catch (err) {
      console.error('Error fetching command centre data:', err);
      setError('Unable to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, getOrgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle AI action
  const handleAIAction = async (queryType, label) => {
    setSelectedPrompt(label);
    setShowAIModal(true);
    setAiResponse('');
    setAiLoading(true);

    const orgId = getOrgId();
    const headers = getAuthHeaders();

    if (!orgId || !headers.Authorization || headers.Authorization === 'Bearer null') {
      setAiResponse('Please log in to use AI features.');
      setAiLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/command-centre/${orgId}/ai-insight`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query_type: queryType })
      });

      if (response.ok) {
        const data = await response.json();
        setAiResponse(data.content);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setAiResponse(errorData.detail || 'Unable to get AI insights at this time.');
      }
    } catch (err) {
      console.error('AI insight error:', err);
      setAiResponse('AI service temporarily unavailable. Please try again later.');
    } finally {
      setAiLoading(false);
    }
  };

  // Handle AIOrb submit
  const handleOrbSubmit = (value) => {
    handleAIAction('get_advice', value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getAlerts = () => {
    if (!summary?.attention_items) return [];
    return summary.attention_items.filter(
      item => item.type === 'overdue_invoice' || 
              item.type === 'cashflow_warning' || 
              item.type === 'upcoming_job'
    ).slice(0, 5);
  };

  const safeToSpend = summary?.financial_overview?.safe_to_spend || 0;
  const cashPosition = summary?.financial_overview?.cash_position || 0;
  const alerts = getAlerts();

  if (loading) {
    return (
      <div className="cc-container" data-testid="command-centre-loading">
        <section className="cc-hero">
          <h1 className="cc-title">Command Centre</h1>
          <p className="cc-subtitle">Loading your business overview...</p>
        </section>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--tl-accent-0)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="cc-container" data-testid="command-centre">

      {/* Hero — AIOrb */}
      <section className="cc-hero" data-testid="cc-hero">
        <h1 className="cc-title">Command Centre</h1>
        <AIOrb onSubmit={handleOrbSubmit} placeholder="Ask TradeLife AI..." />
      </section>

      {/* Quick Actions — 2x2 grid */}
      <section className="cc-quick-actions" data-testid="cc-quick-actions">
        {quickActions
          .filter(btn => !btn.requiresFinance || canAccessFinance)
          .map((btn) => (
          <div
            key={btn.id}
            className="cc-action-btn"
            onClick={() => handleAIAction(btn.id, btn.label)}
            data-testid={`cc-action-${btn.id}`}
          >
            <btn.icon size={22} style={{ color: 'var(--tl-accent-0)' }} />
            <span>{btn.label}</span>
          </div>
        ))}
      </section>

      {/* Attention Needed */}
      <section className="cc-attention" data-testid="cc-attention">
        <GlassPanel 
          title="Attention Needed" 
          titleIcon={AlertCircle}
          headerAction={<span className="widget-badge" data-testid="alerts-count">{alerts.length}</span>}
        >
          {alerts.length > 0 ? (
            <div className="list-container">
              {alerts.map((alert) => (
                <Link key={alert.id} to={alert.link || '#'} className="list-item" data-testid={`alert-item-${alert.id}`}>
                  <div className="list-item-content">
                    <div className="list-item-title">{alert.title}</div>
                    <div className="list-item-subtitle">{alert.description}</div>
                  </div>
                  <span className={`status-badge ${alert.severity === 'high' ? 'overdue' : alert.severity === 'medium' ? 'pending' : 'active'}`}>
                    {alert.type.replace('_', ' ')}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState 
              icon={CheckCircle}
              title="All clear" 
              description="No alerts at this time"
              showGraphic={false}
            />
          )}
        </GlassPanel>
      </section>

      {/* Active Jobs */}
      <section className="cc-active-jobs" data-testid="cc-active-jobs">
        <GlassPanel 
          title="Jobs Today" 
          titleIcon={Briefcase}
          headerAction={<span className="widget-badge" data-testid="jobs-today-count">{jobsToday.length}</span>}
        >
          {jobsToday.length > 0 ? (
            <div className="list-container">
              {jobsToday.map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="list-item" data-testid={`job-item-${job.id}`}>
                  <div className="list-item-content">
                    <div className="list-item-title">{job.title}</div>
                    <div className="list-item-subtitle">
                      <Clock size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                      {job.time}
                      {job.address && ` \u2022 ${job.address}`}
                    </div>
                  </div>
                  <span className={`status-badge ${job.status === 'IN_PROGRESS' ? 'active' : 'pending'}`}>
                    {job.status_display}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState 
              title="No jobs today" 
              description="Schedule jobs to see them here"
              showGraphic={false}
            />
          )}
        </GlassPanel>

        {/* Active Projects */}
        <GlassPanel 
          title="Active Projects" 
          titleIcon={Briefcase}
          headerAction={<span className="widget-badge" data-testid="active-projects-count">{summary?.active_projects?.length || 0}</span>}
        >
          {summary?.active_projects?.length > 0 ? (
            <div className="list-container">
              {summary.active_projects.slice(0, 5).map((project) => (
                <Link key={project.id} to={`/jobs/${project.id}`} className="list-item" data-testid={`project-item-${project.id}`}>
                  <div className="list-item-content">
                    <div className="list-item-title">{project.title}</div>
                    <div className="list-item-subtitle">
                      {project.profit_indicator && project.profit_indicator !== 'unknown' && (
                        <span style={{ 
                          color: project.profit_indicator === 'positive' ? 'var(--status-success)' : 
                                 project.profit_indicator === 'negative' ? 'var(--status-danger)' : 
                                 'var(--text-muted)'
                        }}>
                          {project.profit_indicator === 'positive' && <TrendingUp size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                          {project.profit_indicator === 'negative' && <TrendingDown size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                        </span>
                      )}
                      {project.status_display}
                    </div>
                  </div>
                  <span className={`status-badge ${
                    project.status === 'IN_PROGRESS' ? 'active' : 
                    project.status === 'COMPLETED' ? 'completed' : 
                    'pending'
                  }`}>
                    {project.status_display}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState 
              title="No active projects" 
              description="Create jobs to see them here"
              showGraphic={false}
            />
          )}
        </GlassPanel>
      </section>

      {/* Finance — simplified mobile-first */}
      {canAccessFinance && (
        <section className="cc-finance" data-testid="cc-finance">
          <Link to="/finance" style={{ textDecoration: 'none' }}>
            <GlassPanel title="Financial Snapshot" titleIcon={Wallet}>
              <div className="cc-finance-grid">
                <div className="cc-finance-item">
                  <span className="cc-finance-label">Business Health</span>
                  <span className="cc-finance-value" style={{ color: cashPosition >= 0 ? 'var(--status-success)' : 'var(--status-danger)' }}>
                    {formatCurrency(cashPosition)}
                  </span>
                </div>
                <div className="cc-finance-item">
                  <span className="cc-finance-label">Safe to Spend</span>
                  <span className="cc-finance-value" style={{ color: 'var(--tl-accent-0)' }}>
                    {formatCurrency(safeToSpend)}
                  </span>
                </div>
              </div>
            </GlassPanel>
          </Link>

          {/* Quotes Awaiting */}
          <GlassPanel 
            title="Quotes Awaiting" 
            titleIcon={FileText}
            headerAction={<span className="widget-badge" data-testid="quotes-awaiting-count">{quotesAwaiting.length}</span>}
          >
            {quotesAwaiting.length > 0 ? (
              <div className="list-container">
                {quotesAwaiting.map((quote) => (
                  <Link key={quote.id} to={`/quotes/${quote.id}`} className="list-item" data-testid={`quote-item-${quote.id}`}>
                    <div className="list-item-content">
                      <div className="list-item-title">{quote.client_name}</div>
                      <div className="list-item-subtitle">
                        {quote.days_waiting} day{quote.days_waiting !== 1 ? 's' : ''} ago
                      </div>
                    </div>
                    <div className="list-item-meta">
                      <span className="list-item-amount">{formatCurrency(quote.amount)}</span>
                      <ArrowRight size={16} style={{ color: 'var(--tl-accent-0)' }} />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState 
                title="No pending quotes" 
                description="Quotes awaiting response will appear here"
                showGraphic={false}
              />
            )}
          </GlassPanel>
        </section>
      )}

      {/* AI Insights Modal */}
      {showAIModal && (
        <div className="ai-modal-overlay" onClick={() => setShowAIModal(false)} data-testid="ai-modal-overlay">
          <div className="ai-modal" onClick={e => e.stopPropagation()} data-testid="ai-modal">
            <button 
              onClick={() => setShowAIModal(false)}
              style={{ 
                position: 'absolute', 
                top: 'var(--space-md)', 
                right: 'var(--space-md)',
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer'
              }}
              data-testid="ai-modal-close"
            >
              <X size={20} />
            </button>
            <div className="ai-modal-icon">
              <Bot size={32} />
            </div>
            <h3>AI Insights</h3>
            <p style={{ color: 'var(--tl-accent-0)', fontWeight: 500 }}>&quot;{selectedPrompt}&quot;</p>
            <div style={{ 
              padding: 'var(--space-lg)', 
              background: 'var(--tl-glass-bg)', 
              borderRadius: 'var(--tl-radius)',
              color: 'var(--tl-text-1)',
              fontSize: '0.875rem',
              textAlign: 'left',
              lineHeight: 1.6,
              maxHeight: '300px',
              overflowY: 'auto'
            }} data-testid="ai-modal-content">
              {aiLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)' }}>
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Getting AI insights...</span>
                </div>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{aiResponse}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommandCentre;
